import { useState, useEffect } from 'react'
import { PDFViewer } from '@react-pdf/renderer'
import { fetchFarmInformation, fetchInvoiceWithSales } from '../supabase-client'
import { formatInvoiceDisplayName } from '../utils/invoiceUtils.js'
import InvoiceTemplate from './invoice/InvoiceTemplate'
import InvoicePDFGenerator from './invoice/InvoicePDFGenerator.jsx'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import PDFViewerModal from '@/components/PDFViewerModal'
import './InvoiceDashboard.css'

export default function HistoricalInvoiceModal({
  invoice,
  onClose,
  onNavigate,
  currentIndex,
  totalInvoices
}) {
  const [farmInfo, setFarmInfo] = useState(null)
  const [invoiceRecord, setInvoiceRecord] = useState(null) // Complete invoice from DB
  const [invoiceData, setInvoiceData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [downloadState, setDownloadState] = useState('default') // 'default', 'downloading', 'success', 'error'

  useEffect(() => {
    loadData()
  }, [invoice.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load farm information and invoice data in parallel
      const [farmData, invoiceDetails] = await Promise.all([
        fetchFarmInformation(),
        fetchInvoiceWithSales(invoice.id)
      ])

      if (!farmData) {
        throw new Error('No farm information found. Please set up farm information in Settings.')
      }

      if (!farmData.business_name) {
        throw new Error('Business name is required. Please update farm information in Settings.')
      }

      setFarmInfo(farmData)
      setInvoiceRecord(invoiceDetails.invoice) // Store complete invoice record
      setInvoiceData(invoiceDetails.invoiceData)

    } catch (err) {
      console.error('Historical invoice modal error:', err)
      setError('Failed to load invoice data: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    setDownloadState('downloading')
    try {
      // If a stored PDF exists (Xero or legacy), download it directly
      const storedUrl = invoiceRecord?.xero_pdf_url || invoiceRecord?.pdf_url
      if (storedUrl) {
        const response = await fetch(storedUrl)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${invoiceRecord.invoice_no}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        setDownloadState('success')
        setTimeout(() => setDownloadState('default'), 2000)
        return
      }

      // Otherwise generate on-the-fly
      if (!farmInfo || !invoiceData) {
        setError('Invoice data not loaded. Cannot generate PDF.')
        setDownloadState('default')
        return
      }

      const result = await InvoicePDFGenerator.downloadPDF(invoiceData, farmInfo, {
        filename: `${invoiceData.invoiceNumber}_${invoiceData.customer.name.replace(/\s+/g, '_')}.pdf`
      })

      if (result && result.success) {
        console.log('PDF downloaded successfully:', result.filename)
        setDownloadState('success')
        setTimeout(() => setDownloadState('default'), 2000)
      } else {
        throw new Error(result?.error || 'Unknown download error')
      }
    } catch (error) {
      console.error('PDF download error:', error)
      setDownloadState('error')
      setError('Failed to download PDF: ' + (error.message || 'Unknown error'))
      setTimeout(() => setDownloadState('default'), 3000)
    }
  }


  if (loading) {
    return (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Loading Invoice...</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-8 space-x-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p>Loading invoice data...</p>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (error) {
    return (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Error Loading Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p>⚠️ {error}</p>
            </div>
            <div className="flex justify-center">
              <Button variant="outline" onClick={loadData}>Retry</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // Prepare PDF content
  // Priority: 1) Xero PDF  2) Legacy uploaded PDF  3) Generate on-the-fly
  const storedPdfUrl = invoiceRecord?.xero_pdf_url || invoiceRecord?.pdf_url
  const pdfContent = storedPdfUrl ? (
    <iframe
      src={storedPdfUrl}
      width="100%"
      height="100%"
      title="Invoice PDF"
      className="border-0"
    />
  ) : (
    farmInfo && invoiceData && (
      <PDFViewer width="100%" height="100%">
        <InvoiceTemplate
          invoiceData={invoiceData}
          farmInfo={farmInfo}
          miniStatement={[]}
          showMiniStatement={false}
        />
      </PDFViewer>
    )
  )

  return (
    <PDFViewerModal
      isOpen={true}
      onClose={onClose}
      title={invoiceRecord ? formatInvoiceDisplayName(invoiceRecord) : 'Loading...'}
      pdfContent={pdfContent}
      onDownload={storedPdfUrl || (farmInfo && invoiceData) ? handleDownload : undefined}
      downloadState={downloadState}
      onPrevious={onNavigate ? () => onNavigate('previous') : undefined}
      onNext={onNavigate ? () => onNavigate('next') : undefined}
      hasPrevious={currentIndex > 0}
      hasNext={currentIndex < totalInvoices - 1}
    />
  )
}